import {
    Divider,
    IconChevronLeft,
    IconChevronRight,
    Pagination,
    PaginationButton,
} from '@zillow/constellation';
import React from 'react';

import { buildPaginatedLink } from './buildPaginatedLink';

interface PostsPaginationProps {
    page: number;
    pages: number;
    baseLink: string;
    query?: Record<string, string>;
    incrementPaginationLinks?: boolean;
}

/**
 * Renders a Pagination component for Posts in Categories / Tags
 *
 * @param {Object} props Component props
 * @param {Number} props.page The current page number
 * @param {Number} props.pages The total number of pages
 * @param {String} props.baseLink The base URL for the pagination links
 * @param {Object} props.query The query parameters for the pagination links
 *
 * @returns {ReactElement} Pagination component
 */
export default function PostsPagination({
    page,
    pages,
    baseLink,
    query,
    incrementPaginationLinks,
}: PostsPaginationProps) {
    if (pages <= 1) {
        return null;
    }

    const pageOffset = incrementPaginationLinks ? 1 : 0;
    return (
        <>
            <Divider marginY="md" />
            <Pagination
                defaultCurrentPageIndex={page - 1}
                totalPages={pages}
                renderNumberButton={({ pageIndex }, buttonState) => {
                    return (
                        <PaginationButton
                            forwardedAs="a"
                            href={buildPaginatedLink({
                                baseLink,
                                query,
                                page: pageIndex + pageOffset,
                            })}
                            title={`Page ${pageIndex + 1}${
                                pageIndex === buttonState.currentPageIndex
                                    ? ', current page'
                                    : ''
                            }`}
                            pageIndex={pageIndex}
                            pressed={buttonState.currentPageIndex === pageIndex}
                        >
                            {pageIndex + 1}
                        </PaginationButton>
                    );
                }}
                renderPreviousButton={({ pageIndex }, buttonState) => {
                    return (
                        <PaginationButton
                            forwardedAs="a"
                            href={
                                buttonState.currentPageIndex === 0
                                    ? '#'
                                    : buildPaginatedLink({
                                          baseLink,
                                          query,
                                          page:
                                              buttonState.currentPageIndex -
                                              1 +
                                              pageOffset,
                                      })
                            }
                            rel="prev"
                            title="Previous page"
                            pageIndex={pageIndex}
                            disabled={buttonState.currentPageIndex === 0}
                        >
                            <IconChevronLeft size="xs" />
                        </PaginationButton>
                    );
                }}
                renderNextButton={({ pageIndex }, buttonState) => {
                    return (
                        <PaginationButton
                            forwardedAs="a"
                            href={
                                buttonState.currentPageIndex ===
                                buttonState.totalPages - 1
                                    ? '#'
                                    : buildPaginatedLink({
                                          baseLink,
                                          query,
                                          page:
                                              buttonState.currentPageIndex +
                                              1 +
                                              pageOffset,
                                      })
                            }
                            rel="next"
                            title="Next page"
                            pageIndex={pageIndex}
                            disabled={
                                buttonState.currentPageIndex ===
                                buttonState.totalPages - 1
                            }
                        >
                            <IconChevronRight size="xs" />
                        </PaginationButton>
                    );
                }}
            />
        </>
    );
}
